import type { QuestionnaireWithResponse } from '@seen/core/models/questionnaireResponse'
import { Separator } from '@seen/ui'
import { Fragment, type JSX, type ReactNode } from 'react'
import { CarePlanQuestionnaireAnswerSuggestion } from '#src/components/care-plan/CarePlanQuestionnaireAnswerSuggestion.tsx'
import { QuestionnaireItemForm } from '#src/components/questionnaire-form/questionnaire-item-form/QuestionnaireItemForm.tsx'
import { QuestionnaireItemDisplay } from '#src/components/QuestionnaireItemDisplay.tsx'
import { useCarePlanQuestionnaireSuggestion } from '#src/hooks/useCarePlanQuestionnaireSuggestion.ts'

// Renders one care-plan demographic questionnaire as a titled block of
// per-item fields. Shared by the Participant Summary panel and the care-plan
// Description questionnaire node so both surfaces render identically.
export function CarePlanDemographicQuestionnaire({
  questionnaire,
  questionnaireResponse,
  readOnly = false,
  hideLinkIds,
  prefix,
}: {
  questionnaire: QuestionnaireWithResponse['questionnaire']
  questionnaireResponse: QuestionnaireWithResponse['questionnaireResponse']
  readOnly?: boolean
  hideLinkIds?: readonly string[]
  // Rendered before the items — used by the panel to inject the Service
  // Allocation day-center schedule summary.
  prefix?: ReactNode
}): JSX.Element {
  const items =
    questionnaire.item?.filter(
      (item) => !hideLinkIds?.includes(item.linkId ?? '')
    ) ?? []

  return (
    /* Three type tiers, largest to smallest: the questionnaire, then every block
       title — whether it is an item's own name or a sub-heading inside a stored
       answer — then the scaffold labels that expect a value. Item titles and the
       space under them are raised here rather than in the shared field
       component, whose label styling belongs to every other questionnaire in the
       product. The descendant selector outranks the field's own padding
       utility, so the sub-heading gap is uniform across item types. */
    <div className="flex flex-col gap-4 [&_label]:pb-3 [&_label]:text-base/6 [&_label]:font-semibold">
      <h4 className="text-xl font-semibold">{questionnaire.title}</h4>
      {/* Every rule on the card draws its air from the surrounding flex gap
          rather than its own margin, so the one under the title and the ones
          between groupings are spaced identically. */}
      <Separator />
      <div className="flex flex-col gap-4">
        {prefix}
        {readOnly ? (
          items.map((item, index) => (
            <Fragment key={item.linkId}>
              {(index > 0 || !!prefix) && <Separator />}
              <QuestionnaireItemDisplay
                item={item}
                label={item.text}
                questionnaireResponse={questionnaireResponse}
              />
            </Fragment>
          ))
        ) : (
          <EditableQuestionnaireItems
            items={items}
            leadingSeparator={!!prefix}
            questionnaire={questionnaire}
            questionnaireResponse={questionnaireResponse}
          />
        )}
      </div>
    </div>
  )
}

/* Split out so the read-only surface never reaches the suggestion hook. That
 * hook needs the current practitioner and the assistant's open resource, which
 * a read-only render (a signed plan, a snapshot) has no reason to require. */
function EditableQuestionnaireItems({
  items,
  leadingSeparator,
  questionnaire,
  questionnaireResponse,
}: {
  items: NonNullable<QuestionnaireWithResponse['questionnaire']['item']>
  // Whether the first item follows a grouping of its own — the caller's prefix —
  // and so needs a rule above it like every later item has.
  leadingSeparator: boolean
  questionnaire: QuestionnaireWithResponse['questionnaire']
  questionnaireResponse: QuestionnaireWithResponse['questionnaireResponse']
}): JSX.Element {
  const suggestion = useCarePlanQuestionnaireSuggestion(
    questionnaire,
    questionnaireResponse
  )

  return (
    <>
      {items.map((item, index) => {
        /* An item with an undecided suggestion renders the suggestion INSTEAD
         * of its field, never alongside it — showing both is what made the
         * clinician read the same scaffold twice. Nothing is lost by the swap:
         * the suggestion renders the item's WHOLE answer, with the proposals
         * attached to the lines they target. The field returns as soon as every
         * atom is accepted or rejected. */
        const suggested = suggestion.answersByLinkId.get(item.linkId ?? '')
        return (
          <Fragment key={item.linkId}>
            {(index > 0 || leadingSeparator) && <Separator />}
            {suggested?.length ? (
              <CarePlanQuestionnaireAnswerSuggestion
                answers={suggested}
                canAccept={suggestion.canAccept}
                currentValue={questionnaireResponse?.getAnswerValue(
                  item.linkId ?? ''
                )}
                item={item}
                onAccept={suggestion.accept}
                onEditLine={(edit) =>
                  suggestion.editLine(item.linkId ?? '', edit)
                }
                onReject={suggestion.reject}
              />
            ) : (
              <QuestionnaireItemForm
                item={item}
                label={item.text}
                questionnaireResponse={questionnaireResponse}
              />
            )}
          </Fragment>
        )
      })}
    </>
  )
}
